import React, {useEffect, useState, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import {scaleLinear, scaleTime, extent, select} from 'd3';
import {isActive, formatYear} from '../../../lib/helpers';

const BottomChart = ({id, data, activeYear, width, height, lang}) => {
  const [bars, setBars] = useState([]);
  const barsRef = useRef();
  const barWidth = Math.ceil(width * 0.0075);

  const draw = useCallback(() => {
    // convert years to valid date inside extent since data are numbers
    // and those are needed to keep track of the active year
    const xExtent = extent(data, ({year}) => formatYear(year));

    const xScale =
      lang === 'en'
        ? scaleTime().domain(xExtent).range([0, width])
        : scaleTime()
            .domain(xExtent)
            .range([width, 0 + 20]);

    const yExtent = extent(data, (d) => d[id]);
    // return 0 if both min and max extent are zeros otherwise use linearScale as usual
    const yScale =
      yExtent[0] === 0 && yExtent[1] === 0
        ? () => 0
        : scaleLinear().domain(yExtent).range([0, height]);

    const bars = data.map((d) => {
      return {
        x: xScale(new Date(`${d.year}`)),
        y: 0,
        height: yScale(d[id]),
        year: d.year,
      };
    });

    setBars(bars);
  }, [data, height, width, id, lang]);

  const animate = useCallback(() => {
    let delayCount = 0;
    select(barsRef.current)
      .selectAll('rect')
      .data(bars)
      .attr('fill-opacity', '0.5')
      .attr('y', (d) => {
        return isActive(d.year, activeYear) ? -height : d.y;
      })
      .transition()
      .attr('fill-opacity', (d) => (isActive(d.year, activeYear) ? '1' : '0.5'))
      .attr('y', (d) => d.y)
      .attr('height', (d) => d.height)
      // delayCount here is used here in conjuction with isActive
      // to make sure only the height of newly entered data is animated
      // and avoid animating everything again.
      .delay((d) => {
        delayCount = isActive(d.year, activeYear)
          ? (delayCount += 1)
          : delayCount;
        return delayCount * 100;
      });
    delayCount = 0;
  }, [activeYear, bars, height]);

  // Redraw only on data change
  useEffect(() => {
    draw();
  }, [data, draw]);

  // Animate only when bars data have changed
  useEffect(() => {
    animate();
  }, [bars, animate]);

  return (
    <svg>
      <g ref={barsRef}>
        {bars.map((d) => (
          <rect
            data-year={d.year}
            key={`wpc-${d.year}`}
            x={d.x}
            y={d.y}
            height={d.height}
            width={barWidth}
            fill='#aaa'
          />
        ))}
      </g>
    </svg>
  );
};

BottomChart.propTypes = {
  data: PropTypes.array,
  activeYear: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  lang: PropTypes.string,
};

export default BottomChart;
